import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInFull as ExpandIcon,
  Hotel as HotelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useChat } from '../../../app/providers/ChatContext';
import Message from './Message';
import MessageInput from './MessageInput';
import { colors } from '../../utils/designSystem';

interface FloatingChatPanelProps {
  onClose: () => void;
  onExpandFull: () => void;
}

const MIN_WIDTH = 360;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 440;
const DEFAULT_HEIGHT = 620;

const MotionBox = motion.create(Box);

const FloatingChatPanel: React.FC<FloatingChatPanelProps> = ({ onClose, onExpandFull }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  const {
    currentThread,
    sendMessage,
    stopStreaming,
    createNewThread,
    state,
    isTyping,
  } = useChat();

  const messages = currentThread?.messages || [];
  const lastMessageContent = messages[messages.length - 1]?.content;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, lastMessageContent]);

  // Resize via top-left corner drag (panel anchored bottom-right)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.width;
    const startH = size.height;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      // Dragging top-left: moving left increases width, moving up increases height
      const deltaX = startX - ev.clientX;
      const deltaY = startY - ev.clientY;
      const maxW = window.innerWidth - 48;
      const maxH = window.innerHeight - 48;
      setSize({
        width: Math.max(MIN_WIDTH, Math.min(startW + deltaX, maxW)),
        height: Math.max(MIN_HEIGHT, Math.min(startH + deltaY, maxH)),
      });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [size.width, size.height]);

  return (
    <MotionBox
      ref={panelRef}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      sx={{
        position: 'fixed',
        bottom: { xs: 0, sm: 24 },
        right: { xs: 0, sm: 24 },
        width: { xs: '100vw', sm: size.width },
        height: { xs: '100vh', sm: size.height },
        maxHeight: { sm: 'calc(100vh - 48px)' },
        zIndex: 1250,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 0, sm: '16px' },
        overflow: 'hidden',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.15), 0 8px 32px rgba(0,0,0,0.12)',
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
        border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.2) : colors.gray[200]}`,
      }}
    >
      {/* Resize handle — top-left corner */}
      {!isMobile && (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 20,
            height: 20,
            cursor: 'nwse-resize',
            zIndex: 10,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 4,
              left: 4,
              width: 8,
              height: 8,
              borderTop: `2px solid ${alpha('#fff', 0.4)}`,
              borderLeft: `2px solid ${alpha('#fff', 0.4)}`,
              borderRadius: '2px 0 0 0',
            },
            '&:hover::after': {
              borderColor: alpha('#fff', 0.8),
            },
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.8) : colors.gray[900],
          color: 'white',
          minHeight: 48,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/logo192-dark.png"
          alt="Valuate AI"
          sx={{ width: 22, height: 22, objectFit: 'contain' }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, color: 'white', fontSize: '0.875rem' }}>
          AI Assistant
        </Typography>

        {/* Hotel context chip */}
        {state.selectedHotelName && (
          <Chip
            icon={<HotelIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
            label={state.selectedHotelName.length > 15
              ? state.selectedHotelName.slice(0, 15) + '...'
              : state.selectedHotelName
            }
            size="small"
            sx={{
              height: 24,
              fontSize: '0.675rem',
              bgcolor: alpha('#fff', 0.15),
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
              maxWidth: 140,
            }}
          />
        )}

        {/* New chat button */}
        <IconButton
          size="small"
          onClick={createNewThread}
          sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
          aria-label="New chat"
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Expand to full page */}
        <IconButton
          size="small"
          onClick={onExpandFull}
          sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
          aria-label="Open full chat"
        >
          <ExpandIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Close */}
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
          aria-label="Close chat"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 1.5,
          fontSize: '0.7rem',
          '& .MuiTypography-root': { fontSize: 'inherit' },
          '& .MuiAvatar-root': { width: 26, height: 26 },
          '& .MuiPaper-root': { px: 1.25, py: 1 },
          // Widen message content to fill panel
          '& .MuiBox-root > .MuiBox-root': { maxWidth: '92% !important' },
          // Scale down message layout spacing
          '& > div > div > div': { mb: 1.25, px: '10px !important' },
          // Scale message text
          '& p': { fontSize: '0.7rem !important', lineHeight: '1.5 !important' },
          '& .MuiTypography-caption': { fontSize: '0.6rem !important' },
          '& h1, & h2, & h3': { fontSize: '0.775rem !important', fontWeight: '600 !important' },
          '& ul, & ol': { pl: '14px !important', '& li': { mb: '1px !important', fontSize: '0.7rem !important' } },
          '& code': { fontSize: '0.625rem !important' },
          '& strong': { fontSize: 'inherit' },
          // Insight cards & score cards compact
          '& table': { fontSize: '0.65rem !important' },
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: colors.gray[300],
            borderRadius: 3,
          },
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box
              component="img"
              src="/logo192-dark.png"
              alt="Valuate AI"
              sx={{ width: 36, height: 36, objectFit: 'contain', mb: 1, mx: 'auto', opacity: 0.4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Ask me anything about your hotels, valuations, or market data.
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => (
            <Message
              key={message.id}
              message={message}
              isLast={index === messages.length - 1}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 1,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.15) : colors.gray[100]}`,
          flexShrink: 0,
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : '#fcfcfd',
          '& input, & textarea': { fontSize: '0.8rem !important' },
          '& .MuiIconButton-root': { width: 32, height: 32 },
        }}
      >
        <MessageInput
          onSendMessage={sendMessage}
          onStop={stopStreaming}
          disabled={state.isLoading}
          isLoading={isTyping}
          placeholder="Ask a question..."
        />
      </Box>
    </MotionBox>
  );
};

export default FloatingChatPanel;
